import { Component } from '@theme/component'

const LINK_SELECTOR = '.policies-nav__link'

class PolicyDocError extends Error {
  /**
   * @param {number} index
   * @param {number} total
   * @param {unknown} received
   */
  constructor(index, total, received) {
    const tag =
      received instanceof Element ? received.tagName.toLowerCase() : 'nothing'
    super(
      `policy-switcher found ${tag} at policyDoc[${index}] of ${total}, expected a <details ref="policyDoc[]"> element`
    )
  }
}

class PolicySwitcher extends Component {
  requiredRefs = ['policyLink', 'policyDoc']

  #onHashChange = () => {
    const index = this.#indexOfHandle(this.#handleFromHash())
    if (index === -1) {
      return
    }

    this.#open(index)
  }

  /** @param {Event} event */
  #onLinkClick = (event) => {
    const target = event.target
    if (!(target instanceof Element)) {
      return
    }

    const link = target.closest(LINK_SELECTOR)
    const index = link === null ? -1 : this.refs.policyLink.indexOf(link)
    if (index === -1) {
      return
    }

    event.preventDefault()
    this.#open(index)
    this.#writeHash(index)
  }

  connectedCallback() {
    super.connectedCallback()
    this.addEventListener('click', this.#onLinkClick)
    window.addEventListener('hashchange', this.#onHashChange)
    this.#open(this.#indexOfHandle(this.#handleFromHash()))
  }

  disconnectedCallback() {
    super.disconnectedCallback()
    this.removeEventListener('click', this.#onLinkClick)
    window.removeEventListener('hashchange', this.#onHashChange)
  }

  /** @param {number} index */
  #open(index) {
    const position = index === -1 ? 0 : index
    const doc = this.refs.policyDoc[position]

    if (!(doc instanceof HTMLDetailsElement)) {
      throw new PolicyDocError(position, this.refs.policyDoc.length, doc)
    }

    doc.open = true
    this.#markCurrentLink(position)
  }

  /** @param {number} index */
  #markCurrentLink(index) {
    this.refs.policyLink.forEach((link, position) => {
      if (position === index) {
        link.setAttribute('aria-current', 'true')
        return
      }
      link.removeAttribute('aria-current')
    })
  }

  /** @param {number} index */
  #writeHash(index) {
    const doc = this.refs.policyDoc[index]
    if (!(doc instanceof HTMLElement)) {
      return
    }

    const handle = doc.dataset.policyHandle
    if (!handle) {
      return
    }

    history.replaceState(null, '', `#${handle}`)
  }

  /**
   * @param {string} handle
   * @returns {number}
   */
  #indexOfHandle(handle) {
    if (!handle) {
      return -1
    }

    return this.refs.policyDoc.findIndex(
      (doc) => doc instanceof HTMLElement && doc.dataset.policyHandle === handle
    )
  }

  /** @returns {string} */
  #handleFromHash() {
    const raw = window.location.hash.slice(1)
    if (!raw) {
      return ''
    }

    try {
      return decodeURIComponent(raw)
    } catch {
      return ''
    }
  }
}

if (!customElements.get('policy-switcher')) {
  customElements.define('policy-switcher', PolicySwitcher)
}
